import { useMutation, useQuery } from '@apollo/client';
import { GridApi, GridRowId } from '@mui/x-data-grid-pro';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { addDays } from 'date-fns';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Currency,
  IntakeRequestLineItemsQuery,
  SvbrgChatMessageStatus,
  SvbrgIntakeRequestLineItemNextInput,
  SvbrgFormStepType,
} from '@/generated/gql/graphql';
import {
  useExtractedRequirements,
  shouldPrePopulateFromRequirements,
} from '~/routes/IntakeDetail/utils/useExtractedRequirements';

import { INTAKE_REQUEST_LINE_ITEM_STEP_NEXT_MUTATION } from './IntakeRequestLineItemStep.mutation.gql';
import { INTAKE_REQUEST_LINE_ITEM_QUERY } from './IntakeRequestLineItemStep.query.gql';
import { IntakeRequestLineItem } from './IntakeRequestLineItemStep.schema';
import { toLineItemSchemaData, gridDataToLineItem } from './IntakeRequestLineItemStep.util';

type UseNextMutationProps = {
  apiRef: MutableRefObject<GridApi>;
  session: any;
  next: (options: { variables: { data: SvbrgIntakeRequestLineItemNextInput } }) => void;
  isLoading: boolean;
};

export const useNextMutation = ({ apiRef, session, next, isLoading }: UseNextMutationProps) => {
  const handleNext = useCallback(() => {
    const rows = Array.from(apiRef?.current.getRowModels().values());
    (async () => {
      await next({
        variables: {
          data: {
            chatSessionId: session.id,
            lineItems: rows.map((row, index) => gridDataToLineItem(row, index)),
          },
        },
      });
    })();
  }, [apiRef, next, session.id]);

  return { handleNext, isLoading };
};

interface UseIntakeRequestLineItemStepProps {
  sessionId: string;
  status?: SvbrgChatMessageStatus;
  chatSessionCompleted: boolean;
}

export const useIntakeRequestLineItemStep = ({ sessionId, status }: UseIntakeRequestLineItemStepProps) => {
  const [loadedData, setLoadedData] = useState<IntakeRequestLineItemsQuery | undefined>(undefined);

  const { data, loading, refetch } = useQuery(INTAKE_REQUEST_LINE_ITEM_QUERY, {
    variables: { id: sessionId },
    skip: status === SvbrgChatMessageStatus.PENDING,
    initialFetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
  });

  useEffect(() => {
    setLoadedData(data);
  }, [data]);

  const [save, { loading: isSaving }] = useMutation(INTAKE_REQUEST_LINE_ITEM_STEP_NEXT_MUTATION);

  const reset = async () => {
    const { data } = await refetch();
    // enforce reset through backend data
    setLoadedData(() => ({ ...data }));
  };

  return {
    data: loadedData,
    loading: loading,
    updating: isSaving,
    save,
    reset,
  };
};

type LineItemValidationMap = Record<GridRowId, boolean>;
export const useValidationTrigger = () => {
  const [validations, setValidations] = useState<LineItemValidationMap>({});

  const shouldValidate = (id: GridRowId) =>
    setValidations((prev) => ({
      ...prev,
      [id]: true,
    }));

  return {
    triggeredValidations: validations,
    shouldValidate,
  };
};

export const useLineItemsData = (
  data: IntakeRequestLineItemsQuery | undefined,
  sessionId?: string,
): {
  lineItems: IntakeRequestLineItem[];
  currency: Currency;
  commodityGroupId?: string | null;
} => {
  // Get extracted requirements for line items
  const { hasData: hasExtractedData, data: extractedData } = useExtractedRequirements({
    chatSessionId: sessionId || '',
    stepType: SvbrgFormStepType.REQUEST_LINE_ITEMS,
    enabled: Boolean(sessionId),
  });

  return useMemo(() => {
    const request = data?.sevenbridges.chatSession.request;

    let lineItems = toLineItemSchemaData(request?.lineItems ?? []);

    // Pre-populate from extracted requirements if no existing line items
    if (lineItems.length === 0 && shouldPrePopulateFromRequirements(hasExtractedData, request?.lineItems)) {
      // we need to re-map dates as they come as strings from the backend
      // usually Date scalar should handle this, but here we have a JSON object thus they stay a string
      const extractedLineItems = extractedData as
        | {
            id: string;
            sequence: number;
            position: string;
            title: string;
            description: string;
            quantity: number;
            expectedPricePerUnit: number;
            unitId: string;
            commodityGroupId: string;
            earlyDeliveryAt: string | null;
            lateDeliveryAt: string | null;
          }[]
        | null;

      const mappedLineItems: IntakeRequestLineItem[] | null =
        extractedLineItems?.map((item) => ({
          ...item,
          earlyDeliveryAt: item.earlyDeliveryAt ? new Date(item.earlyDeliveryAt) : null,
          lateDeliveryAt: item.lateDeliveryAt ? new Date(item.lateDeliveryAt) : addDays(new Date(), 30),

          // set the line item commodity group to default of the request if not provided by extracted data
          commodityGroupId: item.commodityGroupId || request?.commodityGroupId,
        })) ?? null;

      // extractedData is already pre-processed by the backend as an array of line items
      lineItems = mappedLineItems ?? [];
    }

    return {
      lineItems,
      commodityGroupId: request?.commodityGroupId,
      currency: request?.currency ?? (DEFAULT_CURRENCY as Currency),
    };
  }, [data, hasExtractedData, extractedData]);
};
